import React, { useState } from 'react';
import { App, Button, Card, DatePicker, Form, InputNumber, Select, Space } from 'antd';
import { ArrowLeftOutlined, SaveOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { EnterprisePageHeader, WorkspaceLayout } from '@/components/enterprise';
import hrmsService, { hrmsKeys } from '@/services/hrms.service';
import RecruitPeoplePicker from './shared/RecruitPeoplePicker';
import { recruitErrorMessage } from './shared/recruitUi';

/** Manual plan for hires that did not come through an offer (walk-in, transfer, re-hire). */
const OnboardingFormPage: React.FC = () => {
  const navigate = useNavigate();
  const { message } = App.useApp();
  const queryClient = useQueryClient();
  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);
  const { data: templates = [] } = useQuery({ queryKey: hrmsKeys.onboardingTemplates(), queryFn: () => hrmsService.listOnboardingTemplates() });

  const save = async () => {
    try {
      const v = await form.validateFields();
      setSaving(true);
      const created = await hrmsService.startOnboarding({
        ...v,
        join_date: v.join_date ? dayjs(v.join_date).format('YYYY-MM-DD') : undefined,
      });
      message.success(`Onboarding ${created.plan_number} started`);
      await queryClient.invalidateQueries({ queryKey: hrmsKeys.onboarding() });
      navigate(`/hr/onboarding/${created.id}`);
    } catch (e: any) {
      if (e?.errorFields) return;
      message.error(recruitErrorMessage(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <WorkspaceLayout
      header={
        <EnterprisePageHeader
          documentLabel="Start onboarding"
          subtitle="Offers convert automatically — use this for walk-in hires, transfers and re-hires"
          primaryActions={
            <Space>
              <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/hr/onboarding')}>
                Cancel
              </Button>
              <Button type="primary" icon={<SaveOutlined />} loading={saving} onClick={save}>
                Start plan
              </Button>
            </Space>
          }
        />
      }
    >
      <Card title="Onboarding plan" style={{ maxWidth: 640 }}>
        <Form form={form} layout="vertical">
          <Form.Item name="employee_id" label="Employee" rules={[{ required: true, message: 'Select the employee' }]}>
            <RecruitPeoplePicker />
          </Form.Item>
          <Form.Item name="template_id" label="Checklist template" extra="Leave empty to pick the best match for the employee's position, department and employment type.">
            <Select allowClear placeholder="Automatic match" options={templates.filter((t: any) => t.is_active !== false).map((t: any) => ({ value: t.id, label: t.template_name }))} />
          </Form.Item>
          <Form.Item name="join_date" label="Join date" extra="Defaults to the employee's join date; task due dates are calculated from it.">
            <DatePicker style={{ width: '100%' }} format="DD MMM YYYY" />
          </Form.Item>
          <Form.Item name="probation_months" label="Probation (months)">
            <InputNumber min={0} max={12} style={{ width: '100%' }} placeholder="From the employee record" />
          </Form.Item>
        </Form>
      </Card>
    </WorkspaceLayout>
  );
};

export default OnboardingFormPage;
